/**
 * The issuer letterhead, composed once and used everywhere it prints:
 * the on-screen document (src/components/DocumentView.jsx), the generated PDF
 * (netlify/lib/pdf.mjs) and the live preview in Settings.
 *
 * This module exists because those three drifted. DocumentView predated the
 * legal_name / trading_as / website / tax_number fields, so the tax registration
 * number — the one line a Canadian invoice over $30 legally has to carry — printed
 * in the PDF but not on screen. Sharing the function makes that class of bug
 * impossible rather than merely tested for.
 *
 * Plain .js with no imports so Node, Vite and the function bundler can all read it.
 */

/**
 * @param company an organisation row, or an invoice's frozen `issuer` snapshot.
 *   Accepts both the organisations column names and the older `company_*` spelling.
 * @returns { title, lines, taxNumber } — `lines` is already ordered and pruned of
 *   blanks, so a renderer just prints them in sequence.
 */
export function letterheadOf(company = {}) {
  const c = company || {};
  const name = c.name || c.company_name || c.legal_name || "";

  // Don't say the same thing twice: the registered name only appears when it
  // differs from the name clients know.
  const legal = c.legal_name && c.legal_name !== name ? c.legal_name : null;

  const address = String(c.address || c.company_address || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const contact = [c.email || c.company_email, c.phone || c.company_phone]
    .filter(Boolean)
    .join("  ·  ");

  return {
    title: name,
    lines: [
      legal,
      c.trading_as ? `Operating as ${c.trading_as}` : null,
      ...address,
      contact || null,
      c.website || null,
    ].filter((l) => l && String(l).trim()),
    taxNumber: c.tax_number ? `${c.tax_label || "Tax"} No. ${c.tax_number}` : null,
  };
}

/** Flat list including the title and tax line — handy for tests and previews. */
export const letterheadText = (company) => {
  const h = letterheadOf(company);
  return [h.title, ...h.lines, h.taxNumber].filter(Boolean);
};
